import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { k8sResourceService } from '@/services/k8sResourceService'
import SearchableSelector from '@/components/ui/SearchableSelector'

interface GitConfigEditorProps {
  flow: any
  canEdit: boolean
  onSave: (updatedManifest: any, configName: string) => void
}

export default function GitConfigEditor({ flow, canEdit, onSave }: GitConfigEditorProps) {
  const { t } = useTranslation(['pipeline-studio', 'common'])
  const [isEditing, setIsEditing] = useState(false)

  // Edit state
  const [editedEnabled, setEditedEnabled] = useState(false)
  const [editedRepoURL, setEditedRepoURL] = useState('')
  const [editedBranch, setEditedBranch] = useState('')
  const [editedCredentialsSecret, setEditedCredentialsSecret] = useState('')

  // Secrets state
  const [secretNames, setSecretNames] = useState<string[]>([])
  const [secretsLoading, setSecretsLoading] = useState(false)

  // Fetch secrets when editing
  useEffect(() => {
    const fetchSecrets = async () => {
      if (!isEditing || !flow.metadata?.namespace) return

      setSecretsLoading(true)
      try {
        const secretData = await k8sResourceService.getSecrets(flow.metadata.namespace)
        const names = k8sResourceService.extractSecretNames(secretData)
        setSecretNames(names)
      } catch (error) {
        console.error('Failed to fetch secrets:', error)
        setSecretNames([])
      } finally {
        setSecretsLoading(false)
      }
    }

    fetchSecrets()
  }, [isEditing, flow.metadata?.namespace])

  const handleEdit = () => {
    setEditedEnabled(flow.spec?.git?.enabled || false)
    setEditedRepoURL(flow.spec?.git?.repoURL || '')
    setEditedBranch(flow.spec?.git?.branch || 'main')
    setEditedCredentialsSecret(flow.spec?.git?.credentialsSecret || '')
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSave = () => {
    const updatedFlow = {
      ...flow,
      spec: {
        ...flow.spec,
        git: editedEnabled ? {
          enabled: true,
          repoURL: editedRepoURL.trim(),
          branch: editedBranch.trim() || 'main',
          credentialsSecret: editedCredentialsSecret.trim() || undefined,
        } : {
          enabled: false
        }
      }
    }

    if (!updatedFlow.spec.git.credentialsSecret) {
      delete updatedFlow.spec.git.credentialsSecret
    }

    onSave(updatedFlow, t('detail.gitConfig'))
    setIsEditing(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-text-dark">{t('detail.gitConfig', 'Git Configuration')}</h2>
        {canEdit && !isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('detail.edit', 'Edit')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Enabled */}
          <div className="flex items-center">
            <input
              id="gitEnabled"
              type="checkbox"
              checked={editedEnabled}
              onChange={(e) => setEditedEnabled(e.target.checked)}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="gitEnabled" className="ml-2 block text-sm text-gray-900">
              {t('detail.gitEnabled', 'Enabled')}
            </label>
          </div>

          {editedEnabled && (
            <>
              {/* Repository URL */}
              <div>
                <label htmlFor="repoURL" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('detail.gitRepoURL', 'Repository URL')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="repoURL"
                  type="text"
                  value={editedRepoURL}
                  onChange={(e) => setEditedRepoURL(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  placeholder="https://github.com/user/repo.git"
                  required
                />
              </div>

              {/* Branch */}
              <div>
                <label htmlFor="branch" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('detail.gitBranch', 'Branch')}
                </label>
                <input
                  id="branch"
                  type="text"
                  value={editedBranch}
                  onChange={(e) => setEditedBranch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  placeholder="main"
                />
              </div>

              {/* Credentials Secret */}
              <div>
                <SearchableSelector
                  title={t('detail.gitCredentialsSecret', 'Credentials Secret')}
                  value={editedCredentialsSecret}
                  onChange={(val) => setEditedCredentialsSecret(val)}
                  options={secretNames}
                  placeholder={t('common:flowConfig.selectASecret')}
                  loading={secretsLoading}
                  disabled={false}
                  emptyMessage="No secrets available in this namespace"
                  showCount={false}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Secret should contain 'username' and 'token' keys
                </p>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={editedEnabled && !editedRepoURL.trim()}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('detail.save', 'Save')}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.gitEnabled', 'Enabled')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{flow.spec?.git?.enabled ? 'Yes' : 'No'}</dd>
          </div>
          {flow.spec?.git?.enabled && (
            <>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.gitRepoURL', 'Repository URL')}</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono break-all">{flow.spec.git.repoURL || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.gitBranch', 'Branch')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{flow.spec.git.branch || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.gitCredentialsSecret', 'Credentials Secret')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{flow.spec.git.credentialsSecret || '-'}</dd>
              </div>
            </>
          )}
        </dl>
      )}
    </div>
  )
}
